import { useState, useEffect, useRef, useCallback } from "react";
import {
  ExcludedDomainsEditor,
  ExcludedDomainsEditorViewModel,
} from "./excluded-domains-editor.component";
import { useApiClient } from "../../../../data/clients/ApiClient";
import { ExcludedEmailDomainDto } from "../../../../data/models/responses/ExcludedEmailDomainDto";

interface WrapperProps {
  disabled: boolean;
  initialState: boolean;
}

export function ExcludedDomainsEditorWrapper({
  disabled,
  initialState,
}: WrapperProps) {
  const vm = useViewModel(disabled, initialState);
  return <ExcludedDomainsEditor {...vm} />;
}

function useViewModel(
  disabled: boolean,
  initialState: boolean
): ExcludedDomainsEditorViewModel {
  const [toggleState, setToggleState] = useState(initialState);
  const [excludedDomains, setExcludedDomains] = useState<
    ExcludedEmailDomainDto[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const apiClient = useApiClient();

  // Use ref to avoid recreating functions on every render
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;

  const loadExcludedDomains = async () => {
    if (isLoading) return; // Prevent concurrent loads
    try {
      setIsLoading(true);
      const domains = await apiClientRef.current.getExcludedEmailDomains();
      setExcludedDomains(domains);
      setHasLoaded(true);
    } catch (error) {
      console.error("Failed to load excluded domains:", error);
      setExcludedDomains([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddDomain = async (domain: string) => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      const newDomain = await apiClientRef.current.addExcludedEmailDomain({
        domain,
      });
      setExcludedDomains((prev) => [...prev, newDomain]);
    } catch (error) {
      console.error("Failed to add excluded domain:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDomain = async (domain: string) => {
    try {
      await apiClientRef.current.deleteExcludedEmailDomain({ domain });
      setExcludedDomains((prev) => prev.filter((d) => d.domain !== domain));
    } catch (error) {
      console.error("Failed to delete excluded domain:", error);
    }
  };

  const toggle = useCallback(() => {
    setToggleState((prev) => !prev);
  }, []);

  // Load domains when toggle is opened for the first time
  useEffect(() => {
    if (toggleState && !hasLoaded && !isLoading) {
      loadExcludedDomains();
    }
  }, [toggleState, hasLoaded, isLoading]);

  return {
    disabled,
    initialState: toggleState,
    onToggle: toggle,
    excludedDomains,
    onAddDomain: handleAddDomain,
    onDeleteDomain: handleDeleteDomain,
    isLoading,
  };
}
